import { createSelector } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import { sortBy } from 'ramda'
import { AppRootState } from 'src/store/store'
import {
  ChartType,
  AssignmentUserProgress,
  AssignmentCourseProgress,
  AssignmentProgressMatrix,
  MatrixCellData,
} from './types'

/**
 * Base selector for analytics state
 */
export let selectAnalytics = (state: AppRootState) => state.analytics

/**
 * Filter selectors
 */
export let selectSelectedLinkedServiceID = createSelector(
  [selectAnalytics],
  (analytics) => analytics.selectedLinkedServiceID,
)

export let selectViewMode = createSelector(
  [selectAnalytics],
  (analytics) => analytics.viewMode,
)

export let selectTimeRange = createSelector(
  [selectAnalytics],
  (analytics) => analytics.timeRange,
)

export let selectCustomDateRange = createSelector(
  [selectAnalytics],
  (analytics) => ({
    startDate: analytics.customStartDate,
    endDate: analytics.customEndDate,
  }),
)

export let selectSelectedUsers = createSelector(
  [selectAnalytics],
  (analytics) => analytics.selectedUsers,
)

export let selectSelectedCategories = createSelector(
  [selectAnalytics],
  (analytics) => analytics.selectedCategories,
)

export let selectSelectedLicenseTypes = createSelector(
  [selectAnalytics],
  (analytics) => analytics.selectedLicenseTypes,
)

export let selectSelectedGroups = createSelector(
  [selectAnalytics],
  (analytics) => analytics.selectedGroups,
)

/**
 * Data selectors
 */
export let selectCeActivities = createSelector(
  [selectAnalytics],
  (analytics) => analytics.ceActivities,
)

export let selectTimelineData = createSelector(
  [selectAnalytics],
  (analytics) => analytics.timelineData,
)

export let selectAssignmentEvents = createSelector(
  [selectAnalytics],
  (analytics) => analytics.assignmentEvents,
)

export let selectKpiData = createSelector(
  [selectAnalytics],
  (analytics) => analytics.kpiData,
)

export let selectCategories = createSelector(
  [selectAnalytics],
  (analytics) => analytics.categories,
)

export let selectLicenseTypes = createSelector(
  [selectAnalytics],
  (analytics) => analytics.licenseTypes,
)

/**
 * UI state selectors
 */
export let selectIsProcessingData = createSelector(
  [selectAnalytics],
  (analytics) => analytics.isProcessingData,
)

export let selectErrorMessage = createSelector(
  [selectAnalytics],
  (analytics) => analytics.errorMessage,
)

export let selectAlertMessage = createSelector(
  [selectAnalytics],
  (analytics) => analytics.alertMessage,
)

/**
 * Modal state selectors
 */
export let selectUserDetailsModal = createSelector(
  [selectAnalytics],
  (analytics) => ({
    selectedUserForDetails: analytics.selectedUserForDetails,
    userDetailsOpen: analytics.userDetailsOpen,
  }),
)

export let selectDayActivitiesModal = createSelector(
  [selectAnalytics],
  (analytics) => ({
    selectedDayData: analytics.selectedDayData,
    dayActivitiesOpen: analytics.dayActivitiesOpen,
  }),
)

export let selectAssignmentEventModal = createSelector(
  [selectAnalytics],
  (analytics) => ({
    selectedAssignmentEvent: analytics.selectedAssignmentEvent,
    assignmentEventOpen: analytics.assignmentEventOpen,
  }),
)

/**
 * Download state selectors
 */
export let selectDownloadState = createSelector(
  [selectAnalytics],
  (analytics) => ({
    downloading: analytics.downloading,
    exportingCsv: analytics.exportingCsv,
  }),
)

/**
 * Computed/derived selectors
 */

/**
 * Determine which chart type to show based on selected filters
 */
export let selectActiveChartType = createSelector(
  [selectSelectedGroups, selectSelectedUsers],
  (selectedGroups, selectedUsers): ChartType => {
    if (selectedGroups.length > 0) {
      return 'group'
    }
    if (selectedUsers.length > 0) {
      return 'user'
    }
    return 'license'
  },
)

/**
 * Get chart data based on active chart type
 */
export let selectChartData = createSelector(
  [
    selectCeActivities,
    selectActiveChartType,
    selectCategories,
    selectLicenseTypes,
    selectSelectedUsers,
    selectSelectedGroups,
    (state: AppRootState) => state.manage.groups,
  ],
  (
    ceActivities,
    activeChartType,
    categories,
    licenseTypes,
    selectedUsers,
    selectedGroups,
    groups,
  ) => {
    if (!ceActivities.length) {
      return []
    }

    switch (activeChartType) {
      case 'license':
        // Stacked Bar Chart by License Type
        return licenseTypes
          .map((licenseType) => {
            let baseData: { [key: string]: any } = { licenseType }

            // Add CE hours for each category for this license type
            let categoryHours = categories.reduce(
              (acc, category) => {
                let hours = ceActivities
                  .filter(
                    (activity) =>
                      activity.licenseTypes.includes(licenseType) &&
                      activity.category === category,
                  )
                  .reduce((sum, activity) => sum + activity.ceHours, 0)
                return { ...acc, [category]: hours }
              },
              {} as Record<string, number>,
            )

            return { ...baseData, ...categoryHours }
          })
          .filter((item) => {
            // Only include license types that have some hours
            let totalHours = categories.reduce(
              (sum, cat) => sum + (item[cat] || 0),
              0,
            )
            return totalHours > 0
          })

      case 'user': {
        // CE Hours over time by month for selected users
        if (selectedUsers.length === 0) return []

        let monthlyData = ceActivities
          .filter((activity) => selectedUsers.includes(activity.userId))
          .reduce(
            (acc, activity) => {
              let monthKey = dayjs(activity.completionDate).format('YYYY-MM')
              let monthLabel = dayjs(activity.completionDate).format('MMM YYYY')

              let existingMonth = acc[monthKey] || {
                timeFrame: monthLabel,
                monthKey,
              }
              let existingCategoryHours = existingMonth[activity.category] || 0

              return {
                ...acc,
                [monthKey]: {
                  ...existingMonth,
                  [activity.category]: existingCategoryHours + activity.ceHours,
                },
              }
            },
            {} as Record<string, any>,
          )

        let monthlyDataArray = Object.values(monthlyData)
        return sortBy((item: any) => item.monthKey, monthlyDataArray)
      }

      case 'group': {
        // Stacked Bar Chart by Group
        let filteredGroups = groups.filter((g) =>
          selectedGroups.includes(g.id || ''),
        )

        return filteredGroups
          .map((group) => {
            let baseData: { [key: string]: any } = { groupName: group.name }

            // Add CE hours for each category for users in this group
            let categoryHours = categories.reduce(
              (acc, category) => {
                let hours = ceActivities
                  .filter(
                    (activity) =>
                      // Check if user belongs to this group
                      group.users?.includes(activity.userId) &&
                      activity.category === category,
                  )
                  .reduce((sum, activity) => sum + activity.ceHours, 0)
                return { ...acc, [category]: hours }
              },
              {} as Record<string, number>,
            )

            return { ...baseData, ...categoryHours }
          })
          .filter((item) => {
            // Only include groups that have some hours
            let totalHours = categories.reduce(
              (sum, cat) => sum + (item[cat] || 0),
              0,
            )
            return totalHours > 0
          })
      }

      default:
        return []
    }
  },
)

/**
 * Get filter description for display
 */
export let selectFilterDescription = createSelector(
  [
    selectTimeRange,
    selectCustomDateRange,
    selectSelectedUsers,
    selectSelectedCategories,
    selectSelectedLicenseTypes,
    selectSelectedGroups,
    (state: AppRootState) => state.manage.users,
    (state: AppRootState) => state.manage.groups,
  ],
  (
    timeRange,
    customDateRange,
    selectedUsers,
    selectedCategories,
    selectedLicenseTypes,
    selectedGroups,
    users,
    groups,
  ) => {
    let filters = [
      // Time range filter
      (() => {
        if (timeRange === 'custom') {
          if (customDateRange.startDate && customDateRange.endDate) {
            return `Time: ${dayjs(customDateRange.startDate).format('MMM DD, YYYY')} - ${dayjs(customDateRange.endDate).format('MMM DD, YYYY')}`
          }
          return 'Time: Custom range'
        }
        let timeLabels = {
          week: 'Last Week',
          month: 'Last Month',
          quarter: 'Last Quarter',
          year: 'Last Year',
        }
        return `Time: ${timeLabels[timeRange]}`
      })(),

      // User filter
      ...(selectedUsers.length > 0
        ? [
            selectedUsers.length === 1
              ? (() => {
                  let user = users.find((u) => u.id === selectedUsers[0])
                  return `User: ${user?.name || 'Unknown'}`
                })()
              : `Users: ${selectedUsers.length} selected`,
          ]
        : []),

      // Category filter
      ...(selectedCategories.length > 0
        ? [
            selectedCategories.length === 1
              ? `Category: ${selectedCategories[0]}`
              : `Categories: ${selectedCategories.length} selected`,
          ]
        : []),

      // License type filter
      ...(selectedLicenseTypes.length > 0
        ? [
            selectedLicenseTypes.length === 1
              ? `License: ${selectedLicenseTypes[0]}`
              : `Licenses: ${selectedLicenseTypes.length} selected`,
          ]
        : []),

      // Group filter
      ...(selectedGroups.length > 0
        ? [
            selectedGroups.length === 1
              ? (() => {
                  let group = groups.find((g) => g.id === selectedGroups[0])
                  return `Group: ${group?.name || 'Unknown'}`
                })()
              : `Groups: ${selectedGroups.length} selected`,
          ]
        : []),
    ]

    return filters.join(' • ')
  },
)

/**
 * Check if data is being loaded from manage slice
 */
export let selectIsLoadingData = createSelector(
  [(state: AppRootState) => state.manage, selectIsProcessingData],
  (manage, isProcessingData) =>
    isProcessingData ||
    manage.linkedServicesStatus === 'pending' ||
    manage.usersStatus === 'pending' ||
    manage.userHoursStatus === 'pending' ||
    manage.certificatesStatus === 'pending' ||
    manage.liveCertificatesStatus === 'pending' ||
    manage.externalCertificatesStatus === 'pending' ||
    manage.sectinsStatus === 'pending' ||
    manage.coursesStatus === 'pending',
)

/**
 * Select user activities for details modal
 */
export let selectUserActivitiesForDetails = createSelector(
  [selectCeActivities, selectUserDetailsModal],
  (ceActivities, userDetailsModal) => {
    if (!userDetailsModal.selectedUserForDetails) {
      return []
    }
    return ceActivities.filter(
      (activity) => activity.userId === userDetailsModal.selectedUserForDetails,
    )
  },
)

/**
 * Assignment progress selectors
 */

/**
 * Calculate user progress for a specific assignment
 */
export let selectAssignmentUserProgress = createSelector(
  [
    (state: AppRootState, assignmentId: string) => assignmentId,
    (state: AppRootState) => state.manage.assignments,
    (state: AppRootState) => state.manage.users,
    (state: AppRootState) => state.manage.completedCourses,
    (state: AppRootState) => state.manage.courses,
  ],
  (assignmentId, assignments, users, completedCourses) => {
    let assignment = assignments.find((a) => a.id === assignmentId)
    if (!assignment) return []

    return assignment.users.map((userId) => {
      let user = users.find((u) => u.id === userId)
      let assignmentCompletedCourses = completedCourses.filter(
        (cc) => cc.assignmentID === assignmentId && cc.userID === userId,
      )

      let completedCourseIds = assignmentCompletedCourses.map(
        (cc) => cc.courseID,
      )
      let totalCourses = assignment.courses.length
      let completedCount = assignmentCompletedCourses.length
      let completionPercentage =
        totalCourses > 0 ? Math.round((completedCount / totalCourses) * 100) : 0

      let lastActivityDate =
        assignmentCompletedCourses.length > 0
          ? // eslint-disable-next-line fp/no-mutating-methods
            assignmentCompletedCourses.sort(
              (a, b) =>
                new Date(b.dateCompleted).getTime() -
                new Date(a.dateCompleted).getTime(),
            )[0].dateCompleted
          : undefined

      return {
        userId,
        userName: user
          ? `${user.firstName} ${user.lastName}`.trim() || 'Unknown User'
          : 'Unknown User',
        userEmail: user?.primaryEmail || '',
        completedCourses: completedCount,
        totalCourses,
        completionPercentage,
        completedCourseIds,
        lastActivityDate,
      } as AssignmentUserProgress
    })
  },
)

/**
 * Calculate course progress for a specific assignment
 */
export let selectAssignmentCourseProgress = createSelector(
  [
    (state: AppRootState, assignmentId: string) => assignmentId,
    (state: AppRootState) => state.manage.assignments,
    (state: AppRootState) => state.manage.courses,
    (state: AppRootState) => state.manage.completedCourses,
  ],
  (assignmentId, assignments, courses, completedCourses) => {
    let assignment = assignments.find((a) => a.id === assignmentId)
    if (!assignment) return []

    return assignment.courses.map((courseId) => {
      let course = courses.find((c) => c.id === courseId)
      let courseCompletions = completedCourses.filter(
        (cc) => cc.assignmentID === assignmentId && cc.courseID === courseId,
      )

      let completedByUserIds = courseCompletions.map((cc) => cc.userID)
      let totalAssignedUsers = assignment.users.length
      let completedByUsers = courseCompletions.length
      let completionPercentage =
        totalAssignedUsers > 0
          ? Math.round((completedByUsers / totalAssignedUsers) * 100)
          : 0

      return {
        courseId,
        courseTitle: course?.name || 'Unknown Course',
        completedByUsers,
        totalAssignedUsers,
        completionPercentage,
        completedByUserIds,
      } as AssignmentCourseProgress
    })
  },
)

/**
 * Calculate overall assignment progress statistics
 */
export let selectAssignmentProgressStats = createSelector(
  [
    (state: AppRootState, assignmentId: string) => assignmentId,
    (state: AppRootState) => state.manage.assignments,
    (state: AppRootState) => state.manage.completedCourses,
    (state: AppRootState) => state.manage.completedAssignments,
  ],
  (assignmentId, assignments, completedCourses, completedAssignments) => {
    let assignment = assignments.find((a) => a.id === assignmentId)
    if (!assignment) return null

    let totalUsers = assignment.users.length
    let totalCourses = assignment.courses.length
    let totalPossibleCompletions = totalUsers * totalCourses

    let actualCompletions = completedCourses.filter(
      (cc) => cc.assignmentID === assignmentId,
    ).length

    let fullyCompletedUsers = completedAssignments.filter(
      (ca) => ca.assignmentID === assignmentId,
    ).length

    let overallCompletionPercentage =
      totalPossibleCompletions > 0
        ? Math.round((actualCompletions / totalPossibleCompletions) * 100)
        : 0

    return {
      totalUsers,
      totalCourses,
      totalPossibleCompletions,
      actualCompletions,
      fullyCompletedUsers,
      overallCompletionPercentage,
    }
  },
)

/**
 * Create progress matrix for assignment showing user vs course completion grid
 */
export let selectAssignmentProgressMatrix = createSelector(
  [
    (state: AppRootState, assignmentId: string) => assignmentId,
    (state: AppRootState) => state.manage.assignments,
    (state: AppRootState) => state.manage.users,
    (state: AppRootState) => state.manage.courses,
    (state: AppRootState) => state.manage.completedCourses,
  ],
  (assignmentId, assignments, users, courses, completedCourses) => {
    let assignment = assignments.find((a) => a.id === assignmentId)
    if (!assignment) return null

    // Get assignment users and courses
    let assignmentUsers = assignment.users.map((userId) => {
      let user = users.find((u) => u.id === userId)
      return {
        userId,
        userName: user
          ? `${user.firstName} ${user.lastName}`.trim() || 'Unknown User'
          : 'Unknown User',
      }
    })

    let assignmentCourses = assignment.courses.map((courseId) => {
      let course = courses.find((c) => c.id === courseId)
      return {
        courseId,
        courseTitle: course?.name || 'Unknown Course',
      }
    })

    // Build the matrix
    let matrix: MatrixCellData[][] = assignmentUsers.map((user) =>
      assignmentCourses.map((course) => {
        let completion = completedCourses.find(
          (cc) =>
            cc.assignmentID === assignmentId &&
            cc.userID === user.userId &&
            cc.courseID === course.courseId,
        )

        return {
          completed: !!completion,
          completionDate: completion?.dateCompleted,
          certificateId: undefined, // Could be added later if needed
        } as MatrixCellData
      }),
    )

    return {
      users: assignmentUsers,
      courses: assignmentCourses,
      matrix,
    } as AssignmentProgressMatrix
  },
)
